import { useMutation } from '@tanstack/react-query';
import useChatRestClient from '@/Api/ChatRestClient';

interface LeadAttributionParams {
  source?: string;
  product_interest?: string;
  [key: string]: any;
}

interface LeadAttributionResponse {
  lead_ref?: string;
  [key: string]: any;
}

const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return document.cookie
    .split('; ')
    .find(c => c.startsWith(name + '='))
    ?.split('=')[1];
};

const buildPayload = (overrides: LeadAttributionParams = {}) => {
  const urlParams =
    typeof window !== 'undefined'
      ? Object.fromEntries(new URLSearchParams(window.location.search))
      : {};

  return {
    ...urlParams,
    fbc: getCookie('_fbc'),
    fbp: getCookie('_fbp'),
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    landing_page:
      typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    product_interest: 'school_of_chess',
    ...overrides,
  };
};

interface UseLeadAttributionOptions {
  // When true, send only the explicit fields the caller passes to `mutate(...)`
  // — skip the auto-collected URL params, cookies, and page metadata. Used by
  // logged-in flows (renewal / new-payment) where the backend enriches the
  // row from the user's NewUserPricingAPILog history.
  minimal?: boolean;
}

export default function useLeadAttribution(
  handleOnSuccess?: (data: LeadAttributionResponse) => void,
  handleOnError?: (error: any) => void,
  options: UseLeadAttributionOptions = {},
) {
  const ChatRestClient = useChatRestClient();
  const { minimal = false } = options;

  const mutation = useMutation<
    LeadAttributionResponse,
    Error,
    LeadAttributionParams | void
  >(
    async (params) => {
      const payload = minimal ? { ...(params || {}) } : buildPayload(params || {});
      const response = await ChatRestClient.post<LeadAttributionResponse>(
        '/respondio/v1/lead-attribution/create/',
        payload,
      );
      return response.data;
    },
    {
      onSuccess: data => {
        if (handleOnSuccess) handleOnSuccess(data);
      },
      onError: error => {
        if (handleOnError) handleOnError(error);
      },
    },
  );

  return {
    ...mutation,
  };
}
